import history from 'instantsearch.js/cjs/lib/routers/history';
import merge from 'lodash/merge';
import { excludeHiddenFacets, getNonQueryObject, queryStringToQueryObject, searchStateToQueryObject, queryObjectToSearchState } from './utils/query-string.js';

/**
 * Custom routing implementation for our algolia setup.
 * The router and stateMapping are used to map Algolia
 * search state to query object (route state), and vice versa.
 *
 * Based on the official Algolia nextjs router:
 * https://github.com/algolia/instantsearch/blob/master/packages/react-instantsearch-router-nextjs/src/index.ts
 */ const createAlgoliaRouter = ({ router , facets , indexName , pageSize , sortIndices , presetQueryString , serverUrl  })=>{
    let lastPushFromThis = false;
    let handler;
    const stateMapping = (index)=>{
        return {
            stateToRoute (uiState) {
                const indexUiState = uiState[index];
                if (!indexUiState) {
                    return {};
                }
                const queryObject = searchStateToQueryObject(indexUiState, {
                    facets,
                    sortIndices,
                    pageSize,
                    presetQueryObject: {}
                });
                return queryObject;
            },
            routeToState (routeState = {}) {
                const state = queryObjectToSearchState(routeState, {
                    facets,
                    sortIndices,
                    pageSize,
                    isCreatingPresetState: true,
                    presetQueryObject: {}
                });
                if (state.configure) {
                    var _page;
                    // SSR Optimization: Only load the current page on server-side, not all previous pages
                    // - Server: offset=(page-1)*pageSize, length=pageSize (loads only page 3 for ?page=3)
                    // - Client: InfinitePagination component sets offset=0 to load all pages 1-3
                    // This reduces SSR payload size for deep pagination (e.g. page 40)
                    const page = (_page = state.configure.page) !== null && _page !== void 0 ? _page : 1;
                    state.configure.page = page;
                    state.configure.offset = Math.min((page - 1) * pageSize, 999);
                    state.configure.length = pageSize;
                }
                return {
                    [index]: state
                };
            }
        };
    };
    return {
        router: history({
            getLocation () {
                if (typeof window === 'undefined') {
                    return new URL(serverUrl);
                }
                return window.location;
            },
            /**
       * This will get called whenever Algolia wants to update the URL of the page (facet changes, pagination etc)
       * Here we make sure we ONLY replace the URL using NextJs router
       * TODO: revisit this when client side routing is working in the codebase
       */ push (url) {
                const parsedUrl = new URL(url);
                const [pathWithoutHash, hash] = (router.asPath || '').split('#');
                const pathname = pathWithoutHash === null || pathWithoutHash === void 0 ? void 0 : pathWithoutHash.split('?')[0];
                const query = parsedUrl.searchParams.toString();
                router.replace({
                    pathname: router.pathname,
                    query
                }, {
                    pathname,
                    query,
                    hash
                }, {
                    shallow: true
                });
                lastPushFromThis = true;
            },
            /**
       * Creates URL from search state. Algolia will call this after we are done
       * mapping our state. Great step for removing unwanted facets and URL cleanup
       */ createURL ({ routeState , location: { href  } , qsModule  }) {
                const filteredState = excludeHiddenFacets(routeState, {
                    facets
                });
                const search = qsModule.stringify(filteredState, {
                    arrayFormat: 'repeat',
                    encoder: (str)=>encodeURIComponent(str)
                });
                const url = new URL(href);
                url.search = search;
                // Append non-Algolia parameters to preserve them in the URL
                const nonAlgoliaParams = getNonQueryObject(href, {
                    facets
                });
                const urlParams = new URLSearchParams(url.search);
                Object.entries(nonAlgoliaParams).forEach(([key, value])=>{
                    if (Array.isArray(value)) {
                        value.forEach((item)=>urlParams.append(key, item)
                        );
                    } else {
                        urlParams.append(key, value);
                    }
                });
                url.search = urlParams.toString();
                return url.toString();
            },
            /**
       * This will get called every time users load or reload the page, or click the browser’s back or next buttons.
       * Parses URL query parameters into algolia search state (server + client)
       * The result will be passed to our state mapping for further processing
       */ parseURL ({ location  }) {
                // overrides for the url
                const presetQueryObject = queryStringToQueryObject(presetQueryString, {
                    facets
                });
                // current url
                const urlQueryObject = queryStringToQueryObject(location.search, {
                    facets
                });
                return merge(urlQueryObject, presetQueryObject);
            },
            start (onUpdate) {
                // Skip router event subscription during server-side rendering to prevent Nextjs router errors.
                // Router events are only needed on the client side to let algolia know we updated the URL
                if (typeof window === 'undefined') {
                    return undefined;
                }
                const initialPathname = router.pathname;
                handler = ()=>{
                    // Only update InstantSearch if we're still on the same page AND this route change
                    // wasn't caused by InstantSearch itself. Without this check, searches would trigger
                    // on pages that don't have InstantSearch mounted.
                    if (router.pathname === initialPathname && !lastPushFromThis) {
                        onUpdate();
                    }
                    lastPushFromThis = false;
                };
                router.events.on('routeChangeComplete', handler);
            },
            dispose () {
                if (handler) {
                    router.events.off('routeChangeComplete', handler);
                }
            },
            cleanUrlOnDispose: true
        }),
        stateMapping: stateMapping(indexName)
    };
};

export { createAlgoliaRouter };
//# sourceMappingURL=create-algolia-router.js.map
